import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { playerService } from '../../../services/playerService';
import { regionalTeamRosterService } from '../../../services/regionalTeamRosterService';
import { matchService } from '../../../services/matchService';
import { tournamentService } from '../../../services/tournamentService';

const STAT_FIELDS = [
  { key: 'points', label: 'PTS', short: true },
  { key: 'rebounds', label: 'REB', short: true },
  { key: 'assists', label: 'AST', short: true },
  { key: 'steals', label: 'STL', short: true },
  { key: 'blocks', label: 'BLK', short: true },
  { key: 'fouls', label: 'PF', leagueOnly: true },
  { key: 'turnovers', label: 'TO', leagueOnly: true },
];

const emptyStats = () => ({
  points: 0,
  rebounds: 0,
  assists: 0,
  steals: 0,
  blocks: 0,
  fouls: 0,
  turnovers: 0,
});

const hasAnyStats = (player) =>
  STAT_FIELDS.some((field) => Number(player[field.key]) > 0);

const mapLeaguePlayer = (player, index) => ({
  id: player.id,
  name: player.userProfile?.fullName || 'Player',
  jerseyNumber: player.jerseyNumber ?? index + 1,
  position: (player.playerPosition || '—').toString().replace(/_/g, ' '),
  ...emptyStats(),
});

const mapRegionalPlayer = (player, index) => ({
  id: player.id,
  name: player.fullName || 'Player',
  jerseyNumber: player.jerseyNumber ?? index + 1,
  position: (player.position || '—').toString().replace(/_/g, ' '),
  ...emptyStats(),
});

const TeamStatsTable = ({
  title,
  players,
  fields,
  onChange,
  teamPoints,
}) => {
  const totals = useMemo(
    () =>
      fields.reduce((acc, field) => {
        acc[field.key] = players.reduce((sum, p) => sum + (Number(p[field.key]) || 0), 0);
        return acc;
      }, {}),
    [players, fields]
  );

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">
            {players.length} player{players.length === 1 ? '' : 's'}
            {teamPoints != null ? ` · Fixture score: ${teamPoints}` : ''}
          </p>
        </div>
        <span className="text-sm font-semibold text-foreground">
          Σ PTS {totals.points || 0}
        </span>
      </div>
      {players.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No roster found for this team.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Player</th>
                <th className="px-3 py-2 font-medium">Pos</th>
                {fields.map((field) => (
                  <th key={field.key} className="px-2 py-2 font-medium text-center w-16">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr key={player.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 text-muted-foreground">{player.jerseyNumber}</td>
                  <td className="px-3 py-2 font-medium text-foreground">{player.name}</td>
                  <td className="px-3 py-2 text-muted-foreground capitalize text-xs">
                    {player.position}
                  </td>
                  {fields.map((field) => (
                    <td key={field.key} className="px-1 py-1.5">
                      <Input
                        type="number"
                        min="0"
                        value={player[field.key]}
                        onChange={(e) =>
                          onChange(player.id, field.key, e.target.value)
                        }
                        className="h-8 text-center px-1"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted/30 text-foreground font-medium">
                <td className="px-3 py-2" colSpan={3}>
                  Team totals
                </td>
                {fields.map((field) => (
                  <td key={field.key} className="px-2 py-2 text-center">
                    {totals[field.key] || 0}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Manual per-player box score entry for a selected league or tournament fixture.
 * Stats are added to season aggregate tables (same pattern as Match Management end-game).
 */
const PlayerMatchStatsRecorder = ({ fixture, onSaved }) => {
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [countAsGamePlayed, setCountAsGamePlayed] = useState(true);

  const isTournament = fixture?.matchType === 'tournament';
  const fields = useMemo(
    () => STAT_FIELDS.filter((f) => !f.leagueOnly || !isTournament),
    [isTournament]
  );

  const loadRosters = useCallback(async () => {
    if (!fixture?.homeTeamId || !fixture?.awayTeamId) {
      setHomePlayers([]);
      setAwayPlayers([]);
      setError('This fixture is missing team information.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (isTournament) {
        const [home, away] = await Promise.all([
          regionalTeamRosterService.getPlayers(fixture.homeTeamId),
          regionalTeamRosterService.getPlayers(fixture.awayTeamId),
        ]);
        setHomePlayers((home || []).map(mapRegionalPlayer));
        setAwayPlayers((away || []).map(mapRegionalPlayer));
      } else {
        const [home, away] = await Promise.all([
          playerService.getByTeam(fixture.homeTeamId),
          playerService.getByTeam(fixture.awayTeamId),
        ]);
        setHomePlayers((home || []).map(mapLeaguePlayer));
        setAwayPlayers((away || []).map(mapLeaguePlayer));
      }
    } catch (err) {
      setError(err.message || 'Failed to load team rosters');
      setHomePlayers([]);
      setAwayPlayers([]);
    } finally {
      setIsLoading(false);
    }
  }, [fixture?.homeTeamId, fixture?.awayTeamId, isTournament]);

  useEffect(() => {
    loadRosters();
  }, [loadRosters]);

  const updatePlayerStat = (side, playerId, field, rawValue) => {
    const value = Math.max(0, parseInt(rawValue, 10) || 0);
    const setter = side === 'home' ? setHomePlayers : setAwayPlayers;
    setter((prev) =>
      prev.map((player) =>
        player.id === playerId ? { ...player, [field]: value } : player
      )
    );
    setSuccess(null);
  };

  const homePointsTotal = homePlayers.reduce((s, p) => s + (Number(p.points) || 0), 0);
  const awayPointsTotal = awayPlayers.reduce((s, p) => s + (Number(p.points) || 0), 0);
  const scoreMismatch =
    fixture?.homeScore != null &&
    fixture?.awayScore != null &&
    (homePointsTotal !== Number(fixture.homeScore) ||
      awayPointsTotal !== Number(fixture.awayScore));

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    const allPlayers = [...homePlayers, ...awayPlayers];
    const withStats = allPlayers.filter(hasAnyStats);
    if (withStats.length === 0) {
      setError('Enter at least one player statistic before saving.');
      return;
    }

    if (!isTournament && !fixture.leagueId) {
      setError('This league match is missing a league ID; cannot save player stats.');
      return;
    }

    setIsSaving(true);
    try {
      if (isTournament) {
        const existingStats = await tournamentService.getRegionalPlayerStatistics();
        const byId = Object.fromEntries(
          (existingStats || []).map((row) => [row.playerId, row])
        );

        await Promise.all(
          withStats.map((player) => {
            const cur = byId[player.id] || {};
            return tournamentService.upsertPlayerStatistics(player.id, {
              gamesPlayed:
                (cur.gamesPlayed || 0) + (countAsGamePlayed ? 1 : 0),
              points: (cur.points || 0) + (player.points || 0),
              rebounds: (cur.rebounds || 0) + (player.rebounds || 0),
              assists: (cur.assists || 0) + (player.assists || 0),
              steals: (cur.steals || 0) + (player.steals || 0),
              blocks: (cur.blocks || 0) + (player.blocks || 0),
            });
          })
        );
      } else {
        await Promise.all(
          withStats.map((player) =>
            matchService.upsertPlayerMatchStats(player.id, fixture.leagueId, {
              gamesPlayed: countAsGamePlayed ? 1 : 0,
              points: player.points || 0,
              rebounds: player.rebounds || 0,
              assists: player.assists || 0,
              steals: player.steals || 0,
              blocks: player.blocks || 0,
              fouls: player.fouls || 0,
              turnovers: player.turnovers || 0,
            })
          )
        );
      }

      setSuccess(
        `Saved stats for ${withStats.length} player${withStats.length === 1 ? '' : 's'}. Values were added to season totals.`
      );
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to save player statistics');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setHomePlayers((prev) => prev.map((p) => ({ ...p, ...emptyStats() })));
    setAwayPlayers((prev) => prev.map((p) => ({ ...p, ...emptyStats() })));
    setSuccess(null);
    setError(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="ClipboardList" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Record Player Match Stats</h2>
            <p className="text-sm text-muted-foreground">
              Enter box-score stats for {fixture.homeName} and {fixture.awayName}. Stats are added
              to season totals.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={loadRosters} disabled={isLoading}>
            Reload Rosters
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={isLoading || isSaving}>
            Clear Inputs
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="px-3 py-2 rounded-lg border bg-success/10 border-success/20 text-success text-sm">
          {success}
        </div>
      )}
      {scoreMismatch && (
        <div className="px-3 py-2 rounded-lg border bg-warning/10 border-warning/20 text-warning text-sm flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
          <span>
            Player points totals ({homePointsTotal}–{awayPointsTotal}) do not match the recorded
            fixture score ({fixture.homeScore}–{fixture.awayScore}). You can still save.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground space-y-2">
          <Icon name="Loader2" size={32} className="mx-auto animate-spin" />
          <p>Loading team rosters...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <TeamStatsTable
              title={fixture.homeName}
              players={homePlayers}
              fields={fields}
              teamPoints={fixture.homeScore}
              onChange={(playerId, field, value) =>
                updatePlayerStat('home', playerId, field, value)
              }
            />
            <TeamStatsTable
              title={fixture.awayName}
              players={awayPlayers}
              fields={fields}
              teamPoints={fixture.awayScore}
              onChange={(playerId, field, value) =>
                updatePlayerStat('away', playerId, field, value)
              }
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={countAsGamePlayed}
                onChange={(e) => setCountAsGamePlayed(e.target.checked)}
                className="rounded border-border"
              />
              Count as a game played for players with entered stats
            </label>
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              loading={isSaving}
              onClick={handleSave}
              disabled={isLoading || (homePlayers.length === 0 && awayPlayers.length === 0)}
            >
              Save Player Stats
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Saving adds these values to existing season statistics. Re-saving the same fixture will
            add the numbers again — clear inputs or use zeros if you only need a one-time entry.
          </p>
        </>
      )}
    </div>
  );
};

export default PlayerMatchStatsRecorder;
